import { useState } from "react"

import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Button } from "@/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { getMemberDisplayName } from "./constants"
import { MissingCountPill } from "./missing-count-pill"

export function MissingTalentCard({
  talent,
  totalMembers,
}: {
  talent: MissingTalentSummary
  totalMembers: number
}) {
  const [isMissingMembersOpen, setIsMissingMembersOpen] = useState(false)
  const severity: "high" | "medium" | "low" =
    talent.missingCount >= Math.max(3, Math.ceil(totalMembers * 0.4))
      ? "high"
      : talent.missingCount >= Math.max(2, Math.ceil(totalMembers * 0.2))
        ? "medium"
        : "low"
  const missingMemberLabels = talent.missingMembers.map((member) =>
    getMemberDisplayName(member),
  )

  return (
    <div className="inline-card grid gap-3 bg-card/65 shadow-(--shadow-card-subtle)">
      <div className="flex items-center justify-between gap-3">
        <div className="text-kicker text-xs text-muted-foreground">
          Tier {talent.tier}
        </div>
        <Dialog
          open={isMissingMembersOpen}
          onOpenChange={setIsMissingMembersOpen}
        >
          <Button
            unstyled
            type="button"
            onClick={() => setIsMissingMembersOpen(true)}
            className="rounded-full transition-transform hover:-translate-y-px focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none"
          >
            <MissingCountPill
              missingCount={talent.missingCount}
              severity={severity}
            />
          </Button>
          <DialogContent className="max-w-lg gap-3 p-0">
            <DialogHeader className="border-b border-border/60 px-5 pt-5 pb-4">
              <DialogTitle className="text-lg font-semibold">
                Missing members for this talent
              </DialogTitle>
              <DialogDescription className="mt-1">
                Tier {talent.tier}: {talent.description}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-5 pb-5">
              <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/35 px-3 py-2 text-xs text-muted-foreground">
                <span>Current gap</span>
                <MissingCountPill
                  missingCount={talent.missingCount}
                  severity={severity}
                />
              </div>
              {missingMemberLabels.length > 0 ? (
                <ul className="grid max-h-72 gap-1.5 overflow-y-auto rounded-xl border border-border/60 bg-background/70 p-3 text-sm">
                  {missingMemberLabels.map((label, index) => (
                    <li
                      key={`${talent.id}-missing-modal-${index}`}
                      className="rounded-lg border border-border/50 bg-card/70 px-2.5 py-1.5"
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-xl border border-dashed border-success/35 bg-success/8 px-3 py-4 text-sm text-success">
                  No members are missing this talent.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="text-sm/6 font-medium text-foreground">
        {talent.description}
      </div>

      <div className="text-xs/5 text-muted-foreground">{talent.reward}</div>
    </div>
  )
}
